import React, { useState, useRef, useEffect } from "react";
import KnowledgeBasePanel from "./KnowledgeBasePanel";
import {
  sendParentTrainingMessage,
  ParentTrainingMessage,
  RAGChunk,
  ParentTrainingCompletionData,
} from "../services/parentTraining";
import "./ParentTrainingPanel.css";

interface ChatDisplayMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  rag_chunks?: RAGChunk[];
  tokens?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
}

const ParentTrainingPanel: React.FC = () => {
  const [messages, setMessages] = useState<ChatDisplayMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [kbPanelOpen, setKbPanelOpen] = useState(false);
  const [selectedChunks, setSelectedChunks] = useState<RAGChunk[]>([]);
  const [collapseAllChunks, setCollapseAllChunks] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messageIdRef = useRef(0);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Handle clicks on source references
  useEffect(() => {
    const handleSourceRefClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("source-reference")) {
        const sourceNum = parseInt(target.getAttribute("data-source") || "0");
        if (sourceNum > 0) {
          const messageElement = target.closest(".message--assistant");
          if (messageElement) {
            const messageId = messageElement.getAttribute("data-message-id");
            const message = messages.find(
              (m) => m.id.toString() === messageId
            );
            if (message?.rag_chunks) {
              handleSourceClick(sourceNum, message.rag_chunks);
            }
          }
        }
      }
    };

    document.addEventListener("click", handleSourceRefClick);
    return () => document.removeEventListener("click", handleSourceRefClick);
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isSending) return;

    const userContent = inputMessage.trim();
    setInputMessage("");

    // Create user message
    const userMsg: ChatDisplayMessage = {
      id: ++messageIdRef.current,
      role: "user",
      content: userContent,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Build conversation history for the API (exclude the current message)
    const conversationHistory: ParentTrainingMessage[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    setStreamingContent("");
    setIsStreaming(true);
    setIsSending(true);

    try {
      await sendParentTrainingMessage(
        userContent,
        conversationHistory,
        // onContent
        (content: string) => {
          setStreamingContent((prev) => prev + content);
        },
        // onComplete
        (data: ParentTrainingCompletionData) => {
          const assistantMsg: ChatDisplayMessage = {
            id: ++messageIdRef.current,
            role: "assistant",
            content: data.content,
            created_at: new Date().toISOString(),
            rag_chunks: data.rag_chunks || undefined,
            tokens: data.tokens,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          setIsStreaming(false);
          setStreamingContent("");
        },
        // onError
        (error: string) => {
          console.error("Parent training chat error:", error);
          setIsStreaming(false);
          setStreamingContent("");
          // Remove the user message on error
          setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        }
      );
    } catch (error) {
      console.error("Parent training chat error:", error);
      setIsStreaming(false);
      setStreamingContent("");
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleViewSources = (chunks: RAGChunk[]) => {
    setSelectedChunks(chunks);
    setKbPanelOpen(true);
    setCollapseAllChunks(true);
    setTimeout(() => setCollapseAllChunks(false), 50);
  };

  const handleSourceClick = (sourceNumber: number, chunks: RAGChunk[]) => {
    setSelectedChunks(chunks);
    setKbPanelOpen(true);
    setTimeout(() => {
      const event = new CustomEvent("navigateToChunk", {
        detail: { chunkIndex: sourceNumber - 1 },
      });
      window.dispatchEvent(event);
    }, 300);
  };

  const handleClearChat = () => {
    if (messages.length === 0) return;
    if (window.confirm("Clear all messages? This cannot be undone.")) {
      setMessages([]);
      setStreamingContent("");
      setIsStreaming(false);
    }
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Reuse markdown formatting from DecisionSupportPanel
  const formatMessageContent = (content: string, ragChunks?: RAGChunk[]) => {
    const lines = content.split("\n");
    const formattedElements: React.ReactElement[] = [];
    let currentList: string[] = [];
    let currentListType: "ul" | "ol" | null = null;
    let inCodeBlock = false;
    let codeBlockLines: string[] = [];

    const flushList = () => {
      if (currentList.length > 0 && currentListType) {
        const ListTag = currentListType;
        formattedElements.push(
          <ListTag key={`list-${formattedElements.length}`}>
            {currentList.map((item, idx) => (
              <li
                key={idx}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(item) }}
              />
            ))}
          </ListTag>
        );
        currentList = [];
        currentListType = null;
      }
    };

    const flushCodeBlock = () => {
      if (codeBlockLines.length > 0) {
        formattedElements.push(
          <pre key={`code-${formattedElements.length}`}>
            <code>{codeBlockLines.join("\n")}</code>
          </pre>
        );
        codeBlockLines = [];
      }
    };

    const formatInlineStyles = (text: string): string => {
      text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      text = text.replace(/__(.*?)__/g, "<strong>$1</strong>");
      text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
      text = text.replace(/_(.*?)_/g, "<em>$1</em>");
      text = text.replace(/`([^`]+)`/g, "<code>$1</code>");
      if (ragChunks && ragChunks.length > 0) {
        text = text.replace(
          /\[Source\s+(\d+(?:\s*,\s*\d+)*)\]/g,
          (_match: string, numbers: string) => {
            const sourceNumbers = numbers
              .split(",")
              .map((n: string) => parseInt(n.trim()));
            const refs = sourceNumbers
              .map(
                (num: number) =>
                  `<span class="source-reference" data-source="${num}" title="View Source ${num}">[${num}]</span>`
              )
              .join(", ");
            return refs;
          }
        );
      }
      return text;
    };

    lines.forEach((line, index) => {
      if (line.trim() === "```" || line.trim().startsWith("```")) {
        if (inCodeBlock) {
          flushCodeBlock();
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockLines.push(line);
        return;
      }

      if (line.match(/^#{1,6}\s/)) {
        flushList();
        const level = line.match(/^#+/)?.[0].length || 1;
        const text = line.replace(/^#+\s/, "");
        const Tag = `h${Math.min(level, 6)}` as keyof JSX.IntrinsicElements;
        formattedElements.push(
          <Tag
            key={index}
            dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
          />
        );
        return;
      }

      const ulMatch = line.match(/^[\s]*[-*+]\s+(.+)/);
      if (ulMatch) {
        if (currentListType !== "ul") {
          flushList();
          currentListType = "ul";
        }
        currentList.push(ulMatch[1]);
        return;
      }

      const olMatch = line.match(/^[\s]*\d+\.\s+(.+)/);
      if (olMatch) {
        if (currentListType !== "ol") {
          flushList();
          currentListType = "ol";
        }
        currentList.push(olMatch[1]);
        return;
      }

      flushList();
      if (line.trim() === "") {
        formattedElements.push(<br key={index} />);
      } else {
        formattedElements.push(
          <p
            key={index}
            dangerouslySetInnerHTML={{ __html: formatInlineStyles(line) }}
          />
        );
      }
    });

    flushList();
    flushCodeBlock();

    return formattedElements;
  };

  // Empty state
  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="parent-training-panel-wrapper">
        <div className="parent-training-panel">
          <div className="parent-training-panel__welcome">
            <div className="welcome-content">
              <span className="welcome-icon">🌸</span>
              <h2>AI Coach for Parent Empowerment</h2>
              <p className="welcome-subtitle">
                Turning everyday moments into opportunities for growth
              </p>
              <p className="welcome-description">
                I'm here 24/7 to help you feel confident, capable, and
                connected. Ask me anything about supporting your child at
                home — from daily routines to managing tough moments — and
                I'll provide personalized, practical strategies you can try
                right away.
              </p>
              <div className="welcome-suggestions">
                <h4>Try asking:</h4>
                <div className="suggestion-chips">
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "How do I handle transitions between activities at home?"
                      )
                    }
                  >
                    🔄 Handling transitions
                  </button>
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "What are some strategies to help my child communicate their needs?"
                      )
                    }
                  >
                    💬 Communication strategies
                  </button>
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "How can I create a visual schedule for my child's daily routine?"
                      )
                    }
                  >
                    📋 Visual schedules
                  </button>
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "What should I do when my child has a meltdown in public?"
                      )
                    }
                  >
                    🤗 Managing meltdowns
                  </button>
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "How can I help my child practice sharing and taking turns?"
                      )
                    }
                  >
                    🤝 Social skills at home
                  </button>
                  <button
                    className="suggestion-chip"
                    onClick={() =>
                      setInputMessage(
                        "How can I build a consistent bedtime routine?"
                      )
                    }
                  >
                    🌙 Bedtime routines
                  </button>
                </div>
              </div>
            </div>

            {/* Input Area in welcome state */}
            <div className="input-area">
              <form onSubmit={handleSubmit} className="input-form">
                <textarea
                  ref={textareaRef}
                  className="input-textarea"
                  placeholder="Ask about strategies for home, routines, transitions..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                <button
                  type="submit"
                  className="send-button"
                  disabled={!inputMessage.trim()}
                >
                  ➤
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-training-panel-wrapper">
      <div
        className={`parent-training-panel ${
          kbPanelOpen ? "parent-training-panel--kb-open" : ""
        }`}
      >
        {/* Header */}
        <div className="pt-header">
          <div className="pt-header__main">
            <span className="pt-header__icon">🌸</span>
            <h2 className="pt-header__title">AI Coach for Parent Empowerment</h2>
          </div>
          <div className="pt-header__actions">
            <button
              className="pt-header__clear-btn"
              onClick={handleClearChat}
              title="Clear conversation"
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="messages-container">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message message--${message.role}`}
              data-message-id={message.id}
            >
              <div className="message__content">
                <div className="message__header">
                  <span className="message__role">
                    {message.role === "user" ? "You" : "AI Assistant"}
                  </span>
                  <span className="message__time">
                    {formatTimestamp(message.created_at)}
                  </span>
                </div>
                <div className="message__text">
                  {formatMessageContent(message.content, message.rag_chunks)}
                </div>
                {message.role === "assistant" && (
                  <div className="message__actions">
                    <button
                      className="message__action-btn"
                      onClick={() => copyToClipboard(message.content)}
                      title="Copy to clipboard"
                    >
                      📋 Copy
                    </button>
                    {message.rag_chunks && message.rag_chunks.length > 0 && (
                      <button
                        className="message__action-btn message__view-sources-btn"
                        onClick={() =>
                          handleViewSources(message.rag_chunks!)
                        }
                        title="View retrieved sources"
                      >
                        📚 Sources ({message.rag_chunks.length})
                      </button>
                    )}
                    {message.tokens && (
                      <span className="message__tokens">
                        {message.tokens.total_tokens.toLocaleString()} tokens
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isStreaming && streamingContent && (
            <div className="message message--assistant">
              <div className="message__content">
                <div className="message__header">
                  <span className="message__role">AI Assistant</span>
                  <span className="message__status">● Streaming...</span>
                </div>
                <div className="message__text">
                  {formatMessageContent(streamingContent)}
                </div>
              </div>
            </div>
          )}
          {isStreaming && !streamingContent && (
            <div className="message message--assistant">
              <div className="message__content">
                <div className="message__header">
                  <span className="message__role">AI Assistant</span>
                </div>
                <div className="message__typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="input-area">
          <form onSubmit={handleSubmit} className="input-form">
            <textarea
              ref={textareaRef}
              className="input-textarea"
              placeholder="Ask about parent training strategies..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              rows={1}
            />
            <button
              type="submit"
              className="send-button"
              disabled={!inputMessage.trim() || isSending}
            >
              {isSending ? "⏳" : "➤"}
            </button>
          </form>
        </div>
      </div>

      {/* Knowledge Base Panel */}
      <KnowledgeBasePanel
        chunks={selectedChunks}
        isOpen={kbPanelOpen}
        onClose={() => setKbPanelOpen(false)}
        collapseAll={collapseAllChunks}
      />
    </div>
  );
};

export default ParentTrainingPanel;
